/**
 * Slice 21b — Cleaner package pickup banner (Package ready → Picked up).
 * No tracking / Amazon / VA copy.
 */
import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useConfirmReorderPickup,
  type PendingReorderForPickup,
} from "@/hooks/useInventoryReorders";
import { toast } from "@/hooks/use-toast";

export function reorderDisplayNames(reorder: PendingReorderForPickup): string[] {
  const fromItems = reorder.items.map((i) => i.item_name.trim()).filter(Boolean);
  if (fromItems.length > 0) return fromItems;
  const note = (reorder.supplier_note || "").trim();
  if (note) return [note];
  return ["Supplies"];
}

export function collectReorderItemIds(reorders: PendingReorderForPickup[]): string[] {
  const ids = new Set<string>();
  for (const r of reorders) {
    for (const i of r.items) {
      if (i.item_id) ids.add(i.item_id);
    }
  }
  return [...ids];
}

interface InventoryReorderPickupBannerProps {
  reorders: PendingReorderForPickup[];
  cleanerId: string;
  propertyId: string;
  /** Compact quiet state after this session’s pickup (optional). */
  justPicked?: boolean;
  onPickedUp?: (info: { itemIds: string[]; names: string[] }) => void;
  className?: string;
}

export function InventoryReorderPickupBanner({
  reorders,
  cleanerId,
  propertyId,
  justPicked = false,
  onPickedUp,
  className,
}: InventoryReorderPickupBannerProps) {
  const confirmAll = useConfirmReorderPickup();
  const [busy, setBusy] = useState(false);

  if (justPicked && reorders.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-[16px] border-[1.5px] border-[#e2eeed] bg-[#f4fbfa] px-4 py-3.5",
          className,
        )}
      >
        <Check className="h-[18px] w-[18px] shrink-0 text-[#15776d] stroke-[2.8]" />
        <span className="text-[15px] font-bold text-[#15776d]">Picked up</span>
      </div>
    );
  }

  if (reorders.length === 0) return null;

  const names = [...new Set(reorders.flatMap(reorderDisplayNames))];
  const namesLine = names.join(" · ");

  const handlePickedUp = async () => {
    if (busy || confirmAll.isPending) return;
    setBusy(true);
    try {
      for (const r of reorders) {
        await confirmAll.mutateAsync({
          reorderId: r.id,
          cleanerId,
          propertyId,
        });
      }
      onPickedUp?.({
        itemIds: collectReorderItemIds(reorders),
        names,
      });
      toast({ title: "Picked up" });
    } catch (e) {
      toast({
        title: "Couldn’t confirm",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-4 py-[18px]",
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="text-[18px] font-bold tracking-[-0.2px] text-[#0f1720]">Package ready</div>
        <div className="text-[15px] font-medium leading-snug text-[#64748b]">{namesLine}</div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => void handlePickedUp()}
        className="flex h-[56px] w-full items-center justify-center rounded-[16px] bg-[#24a89a] text-[18px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Picked up"}
      </button>
    </div>
  );
}
